/**
 * Email Thread Parser
 *
 * Parses email HTML bodies to extract:
 * - The new reply content (content written by the current sender)
 * - The first-level quoted message metadata (From, Sent, To, Subject)
 *
 * Handles variants from real-world email threads:
 * 1. Outlook/Graph API: <hr> + From/Sent/To/Subject headers (EN + NL)
 * 2. Yally Helpdesk template: "Yally Helpdesk - Ticket H{id}" wrapper
 * 3. "Op {date} schreef {email}:" quoting pattern (border-left style)
 */

export interface ParsedEmailMessage {
  from: string;
  sentDate: string;
  to: string;
  subject: string;
  body: string;
}

export interface ParsedEmailThread {
  newContent: string;
  quotedMessages: ParsedEmailMessage[];
}

/**
 * Strips CID (Content-ID) image references from HTML.
 * These are inline image references that are not resolvable outside the original email client.
 */
export function stripCidReferences(html: string): string {
  if (!html) return html;
  try {
    return html
      .replace(/<img[^>]*src=["']cid:[^"']*["'][^>]*\/?>/gi, '')
      .replace(/background-image:\s*url\(["']?cid:[^"')]*["']?\)/gi, '');
  } catch (error) {
    console.error('[emailThreadParser] stripCidReferences failed:', error);
    return html;
  }
}

/**
 * Detects whether a given HTML string contains a quoted block.
 *
 * Supported separator patterns:
 * 1. Outlook-style: <hr> followed by From/Van headers
 * 2. Yally "Op {date} schreef {email}:" in a border-left div
 * 3. Generic border-left blockquote-style divs with "Op ... schreef ..."
 */
function detectQuoteSeparator(html: string): number {
  let earliestIndex = -1;

  // --- Pattern group 1: Outlook-style HR + From/Van headers ---
  const hrPatterns = [
    /<hr\s*\/?>/i,
    /<hr[^>]*>/i,
    /<div[^>]*border-top[^>]*>/i,
  ];

  for (const pattern of hrPatterns) {
    const match = pattern.exec(html);
    if (match && match.index !== undefined) {
      const afterSep = html.slice(match.index + match[0].length);
      if (hasEmailHeader(afterSep)) {
        if (earliestIndex === -1 || match.index < earliestIndex) {
          earliestIndex = match.index;
        }
      }
    }
  }

  // --- Pattern group 2: "Op {date} schreef {email}:" in a border-left div ---
  // Matches: <div style="...border-left: 3px solid #ccc...">...<strong>Op ... schreef ...:</strong>
  const borderLeftQuotePattern =
    /<div[^>]*border-left:\s*3px\s+solid\s+#ccc[^>]*>/i;
  const borderLeftMatch = borderLeftQuotePattern.exec(html);
  if (borderLeftMatch && borderLeftMatch.index !== undefined) {
    // Check that this div contains the "Op ... schreef ..." pattern
    const afterDiv = html.slice(
      borderLeftMatch.index,
      borderLeftMatch.index + 2000
    );
    if (/Op\s+[\d\-\/.,\s:]+\s+schreef\s+/i.test(afterDiv)) {
      // The separator starts at the <br> tags preceding the div (if any)
      const precedingHtml = html.slice(
        Math.max(0, borderLeftMatch.index - 30),
        borderLeftMatch.index
      );
      const brTailMatch = precedingHtml.match(
        /(<br\s*\/?>[\s]*)+$/i
      );
      const sepStart = brTailMatch
        ? borderLeftMatch.index - brTailMatch[0].length
        : borderLeftMatch.index;

      if (earliestIndex === -1 || sepStart < earliestIndex) {
        earliestIndex = sepStart;
      }
    }
  }

  // --- Pattern group 3: Standalone "Op {date} schreef {email}:" without border-left ---
  // Sometimes the pattern appears as a <p> or plain text (not inside a styled div)
  const opSchreefPattern =
    /<(?:p|div)[^>]*>[\s]*(?:<(?:strong|b)>)?Op\s+[\d\-\/.,\s:]+\s+schreef\s+[^<]+:(?:<\/(?:strong|b)>)?/i;
  const opSchreefMatch = opSchreefPattern.exec(html);
  if (opSchreefMatch && opSchreefMatch.index !== undefined) {
    // Walk backwards to include preceding <br> tags
    const precedingHtml = html.slice(
      Math.max(0, opSchreefMatch.index - 30),
      opSchreefMatch.index
    );
    const brTailMatch = precedingHtml.match(
      /(<br\s*\/?>[\s]*)+$/i
    );
    const sepStart = brTailMatch
      ? opSchreefMatch.index - brTailMatch[0].length
      : opSchreefMatch.index;

    if (earliestIndex === -1 || sepStart < earliestIndex) {
      earliestIndex = sepStart;
    }
  }

  return earliestIndex;
}

/**
 * Strips Yally Helpdesk template wrapper, returning only the inner content.
 *
 * IMPORTANT: Only triggers when the Yally template is the OUTERMOST structure
 * (i.e., the HTML itself IS a Yally-sent email). If the Yally template appears
 * deep in the body (e.g., quoted inside another email), it is left untouched.
 *
 * The template structure is:
 *   <div style="font-family: Arial...max-width: 600px...">
 *     <div style="background-color: #F9F0EE...">Yally Helpdesk - Ticket H{id}</div>
 *     <div style="background-color: white...">ACTUAL CONTENT</div>
 *     <div style="...">Let op: footer...</div>
 *   </div>
 */
export function stripYallyTemplateWrapper(html: string): string {
  const trimmed = html.trim();

  // Only strip when the Yally template IS the root structure.
  // Check that the HTML starts with the template's outer div (font-family: Arial + max-width: 600px)
  // AND that "Yally Helpdesk - Ticket H{id}" appears in the first 500 chars (the header section).
  // This prevents false matches on emails that merely QUOTE a Yally template deeper in the body.
  const startsWithTemplateDiv =
    /^<div[^>]*font-family:\s*Arial[^>]*max-width:\s*600px[^>]*>/i.test(trimmed);
  const first500 = trimmed.slice(0, 500);
  const hasYallyHeaderNearTop =
    /Yally\s+Helpdesk\s*-\s*Ticket\s+H\d+/i.test(first500);

  if (!startsWithTemplateDiv || !hasYallyHeaderNearTop) {
    return html;
  }

  try {
    // Extract the content div (the one with background-color: white)
    // It sits between the header div and the footer div
    const contentDivPattern =
      /<div[^>]*background-color:\s*white[^>]*>([\s\S]*?)<\/div>\s*<div[^>]*(?:background-color:\s*#f5f5f5|Let\s+op:)/i;
    const match = contentDivPattern.exec(html);
    if (match && match[1]) {
      return match[1].trim();
    }

    // Fallback: try to find content between the header and footer sections
    // Header ends after "Automatisch bericht van ons helpdesk systeem</p></div>"
    const headerEndPattern =
      /Automatisch\s+bericht\s+van\s+ons\s+helpdesk\s+systeem<\/p>\s*<\/div>/i;
    const headerMatch = headerEndPattern.exec(html);
    if (headerMatch) {
      const afterHeader = html.slice(
        headerMatch.index + headerMatch[0].length
      );
      // Find the footer ("Let op:" section)
      const footerPattern =
        /<div[^>]*>\s*<p[^>]*>\s*<strong>Let\s+op:<\/strong>/i;
      const footerMatch = footerPattern.exec(afterHeader);
      if (footerMatch) {
        const content = afterHeader.slice(0, footerMatch.index);
        // Strip the outer content div wrapper if present
        const innerMatch =
          /<div[^>]*>([\s\S]*)<\/div>\s*$/i.exec(content.trim());
        return innerMatch ? innerMatch[1].trim() : content.trim();
      }
    }
  } catch (error) {
    console.error(
      '[emailThreadParser] stripYallyTemplateWrapper failed:',
      error
    );
  }

  return html;
}

/**
 * Checks whether the given HTML snippet begins with (or shortly after) an email header block.
 */
function hasEmailHeader(html: string): boolean {
  const sample = html.slice(0, 2000);
  return /(<b>|<strong>)?(From|Van|From:|Van:)\s*(<\/b>|<\/strong>)?/i.test(
    sample
  );
}

/**
 * Extracts header value from a quoted block HTML fragment.
 * Handles both bold-wrapped and plain text variants.
 *
 * Supported variants:
 *   <b>From:</b> value
 *   <strong>From:</strong> value
 *   From: value (plain text in a <p>)
 */
function extractHeaderValue(html: string, labels: string[]): string {
  const labelPattern = labels
    .map((l) => l.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');

  const pattern = new RegExp(
    `(?:<(?:b|strong)>)?(?:${labelPattern})(?:</(?:b|strong)>)?\\s*([^<\\n\\r]*(?:<(?!(?:b|strong|p|br|div))[^>]*>[^<]*</[^>]+>)*)`,
    'i'
  );

  const match = pattern.exec(html);
  if (!match) return '';

  return match[1]
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .trim();
}

/**
 * Parses the metadata (From, Sent, To, Subject) from the quoted block HTML.
 */
function parseQuotedMessageMeta(
  quotedHtml: string
): Omit<ParsedEmailMessage, 'body'> {
  return {
    from: extractHeaderValue(quotedHtml, ['From:', 'Van:']),
    sentDate: extractHeaderValue(quotedHtml, ['Sent:', 'Verzonden:']),
    to: extractHeaderValue(quotedHtml, ['To:', 'Aan:']),
    subject: extractHeaderValue(quotedHtml, ['Subject:', 'Onderwerp:']),
  };
}

/**
 * Extracts the body of the quoted message — the content AFTER the header block.
 * Stops before any nested quoted messages to avoid recursion.
 */
function extractQuotedBody(quotedHtml: string): string {
  const headerEndPatterns = [
    /(?:<(?:b|strong)>)?(?:Subject:|Onderwerp:)(?:<\/(?:b|strong)>)?[^\n\r<]*/i,
  ];

  let headerEndIndex = -1;
  for (const pattern of headerEndPatterns) {
    const match = pattern.exec(quotedHtml);
    if (match) {
      headerEndIndex = match.index + match[0].length;
      break;
    }
  }

  if (headerEndIndex === -1) {
    return stripCidReferences(quotedHtml);
  }

  const body = quotedHtml.slice(headerEndIndex);
  return stripCidReferences(body.trim());
}

/**
 * Parses an email HTML body and returns:
 * - `newContent`: The new reply content only (before the first quoted block)
 * - `quotedMessages`: Array containing only the first-level quoted message
 *
 * Handles:
 * 1. Yally Helpdesk template wrappers (stripped before parsing)
 * 2. Outlook-style quoting (<hr> + From/Sent/To/Subject)
 * 3. "Op {date} schreef {email}:" quoting (border-left style)
 *
 * Design decision: We only extract one level of quoting. Nested threads within
 * the quoted message are left untouched — the mail system (Graph API) handles
 * threading via message IDs.
 */
export function parseEmailThread(htmlBody: string): ParsedEmailThread {
  if (!htmlBody) {
    return { newContent: '', quotedMessages: [] };
  }

  try {
    let cleanedHtml = stripCidReferences(htmlBody);

    // Strip Yally template wrapper to access the raw email content
    cleanedHtml = stripYallyTemplateWrapper(cleanedHtml);

    const separatorIndex = detectQuoteSeparator(cleanedHtml);

    if (separatorIndex === -1) {
      // No quoted block found — entire body is new content
      return { newContent: cleanedHtml, quotedMessages: [] };
    }

    const newContent = cleanedHtml.slice(0, separatorIndex).trim();
    const quotedSection = cleanedHtml.slice(separatorIndex).trim();

    const meta = parseQuotedMessageMeta(quotedSection);
    const body = extractQuotedBody(quotedSection);

    const quotedMessage: ParsedEmailMessage = {
      ...meta,
      body,
    };

    return {
      newContent,
      quotedMessages: [quotedMessage],
    };
  } catch (error) {
    console.error('[emailThreadParser] parseEmailThread failed:', error);
    return { newContent: htmlBody, quotedMessages: [] };
  }
}
